import { type CSSProperties, type ReactNode } from 'react';
import { accentPresets, type AccentPreset } from '@vitness/tokens';
import { useTheme, type ThemeMode } from '@vitness/theme';
import { Sun, Moon, Monitor } from '@vitness/icons';

/** Theme dimensions the switcher can expose; order here is the render order. */
export type ThemeSwitcherSection = 'theme' | 'accent' | 'density' | 'surface' | 'icons';

export interface ThemeSwitcherProps {
  /** Which controls to render. Defaults to all five. */
  sections?: ThemeSwitcherSection[];
  /** Optional heading shown above the controls (e.g. `Appearance`). */
  heading?: ReactNode;
  style?: CSSProperties;
}

const MODES: Array<{ id: ThemeMode; label: string }> = [
  { id: 'dark', label: 'Dark' },
  { id: 'light', label: 'Light' },
  { id: 'system', label: 'System' },
];
const DENSITIES = ['tight', 'comfortable'] as const;
const SURFACES = ['flat', 'outlined', 'elevated'] as const;
const DEFAULT_SECTIONS: ThemeSwitcherSection[] = ['theme', 'accent', 'density', 'surface', 'icons'];

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  const { accentRgb } = useTheme();
  const rs = `${accentRgb.r},${accentRgb.g},${accentRgb.b}`;
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      style={{
        padding: '4px 10px',
        borderRadius: 'var(--vn-radius-sm)',
        border: active ? `1px solid rgba(${rs},0.4)` : '1px solid var(--vn-border-default)',
        background: active ? `rgba(${rs},0.08)` : 'transparent',
        color: active ? 'var(--vn-text-primary)' : 'var(--vn-text-secondary)',
        fontFamily: 'var(--vn-font-body)',
        fontSize: 10,
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
    >
      {label}
    </button>
  );
}

function Label({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontFamily: 'var(--vn-font-mono)',
        fontSize: 8,
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: 'var(--vn-text-disabled)',
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Reusable appearance control surface for the active {@link useTheme} theme:
 * mode, accent, density, surface, and icon emphasis. Renders flat (no card) so
 * it can sit inside a sidebar popover, a settings panel, or a standalone page.
 */
export function ThemeSwitcher({ sections = DEFAULT_SECTIONS, heading, style }: ThemeSwitcherProps) {
  const {
    mode,
    themeMode,
    accentName,
    density,
    surface,
    accentIcons,
    setMode,
    setAccent,
    setDensity,
    setSurface,
    setAccentIcons,
    reset,
    accentRgb,
  } = useTheme();
  const rs = `${accentRgb.r},${accentRgb.g},${accentRgb.b}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, ...style }}>
      {heading != null && (
        <div
          style={{
            fontFamily: 'var(--vn-font-mono)',
            fontSize: 8,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'var(--vn-text-disabled)',
          }}
        >
          {heading}
        </div>
      )}

      {sections.includes('theme') && (
        <div>
          <Label>Theme</Label>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            {MODES.map(({ id, label }) => (
              <Chip key={id} label={label} active={themeMode === id} onClick={() => setMode(id)} />
            ))}
            <span style={{ marginLeft: 2, display: 'flex', alignItems: 'center' }}>
              {themeMode === 'system' ? (
                <Monitor size={11} color="var(--vn-text-muted)" />
              ) : mode === 'dark' ? (
                <Moon size={11} color="var(--vn-text-muted)" />
              ) : (
                <Sun size={11} color="var(--vn-text-muted)" />
              )}
            </span>
          </div>
        </div>
      )}

      {sections.includes('accent') && (
        <div>
          <Label>Accent</Label>
          <div style={{ display: 'flex', gap: 4 }}>
            {(Object.entries(accentPresets) as [AccentPreset, string][]).map(([name, hex]) => (
              <button
                key={name}
                type="button"
                aria-label={`Set accent to ${name}`}
                aria-pressed={accentName === name}
                onClick={() => setAccent(name)}
                title={name}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 'var(--vn-radius-sm)',
                  background: hex,
                  border: accentName === name ? `2px solid rgba(${rs},0.8)` : '2px solid transparent',
                  cursor: 'pointer',
                  boxShadow: accentName === name ? `0 0 6px rgba(${rs},0.3)` : 'none',
                  transition: 'all 0.15s ease',
                }}
              />
            ))}
          </div>
        </div>
      )}

      {sections.includes('density') && (
        <div>
          <Label>Density</Label>
          <div style={{ display: 'flex', gap: 4 }}>
            {DENSITIES.map((d) => (
              <Chip key={d} label={titleCase(d)} active={density === d} onClick={() => setDensity(d)} />
            ))}
          </div>
        </div>
      )}

      {sections.includes('surface') && (
        <div>
          <Label>Surface</Label>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {SURFACES.map((s) => (
              <Chip key={s} label={titleCase(s)} active={surface === s} onClick={() => setSurface(s)} />
            ))}
          </div>
        </div>
      )}

      {sections.includes('icons') && (
        <div>
          <Label>Icons</Label>
          <div style={{ display: 'flex', gap: 4 }}>
            <Chip label="Hover" active={!accentIcons} onClick={() => setAccentIcons(false)} />
            <Chip label="Accent" active={accentIcons} onClick={() => setAccentIcons(true)} />
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={reset}
        style={{
          alignSelf: 'flex-start',
          marginTop: 2,
          padding: '4px 10px',
          borderRadius: 'var(--vn-radius-sm)',
          border: '1px solid var(--vn-border-default)',
          background: 'transparent',
          color: 'var(--vn-text-secondary)',
          fontFamily: 'var(--vn-font-body)',
          fontSize: 10,
          fontWeight: 400,
          cursor: 'pointer',
          transition: 'all 0.15s ease',
        }}
      >
        Reset to defaults
      </button>
    </div>
  );
}
